import React, { Fragment, useEffect, useState } from "react";
import { AddStandardIcon, NoPreviewIcon } from "../../../assets/icons/index";
import { IVideo, IStandardVideo } from "../../../model/interface/video";
import Loader from "../../shared/loader/loader";
import { useNavigate, Link } from "react-router";
import Constant from "../../../utils/constant";
import { IStandardVideoData } from "../../../model/interface/standard";
import { DeleteIcon } from "../../../assets/icons/index";
import DeleteModal from "../../shared/common/deleteModal";
import {
  Dialog,
  DialogBackdrop,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { XIcon } from "@heroicons/react/outline";
import ReactPlayer from "react-player";
import {
  associateStandardVideos,
  validateStandardVideoAssociation,
} from "../../../api/standard.service";
import { toast } from "react-toastify";
import { useConfirmation } from "../../shared/confirmation/confirmationService";

interface IVideosProps {
  standard: IStandardVideoData;
  languageId?: number;
}

function StandardVideos(props: IVideosProps) {
  const defaultStandardVideo: IStandardVideo = {
    videoId: 0,
    standardId: 0,
  };

  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [videos, setVideos] = useState<IVideo[]>([]);
  const [showDeletePopup, setShowDeletePopup] = useState<boolean>(false);
  const [videoToDelete, setVideoDelete] =
    useState<IStandardVideo>(defaultStandardVideo);
  const [togglePopup, setTogglePopup] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [vttUrl, setVttUrl] = useState<string | null>(null);
  const [showAssociateVideoPopup, setShowAssociateVideoPopup] =
    useState<boolean>(false);
  const [videoIds, setVideoIds] = useState<string>("");
  const confirm = useConfirmation();
  useEffect(() => {
    setTimeout(() => bindVideos(props.standard.standardId), 1500);
  }, [props.standard.standardId]);

  const handleRedirect = (video?: IVideo) => {
    if (!video) {
      const defaultData: IVideo = {
        videoId: 0,
        standardId: props.standard.standardId || 0,
        standardCode: "",
        transcript: "",
        spanishTranscript: "",
        videoUrl: "",
        spanishVideoUrl: "",
        standardName: props.standard.standardName || "",
        title: "",
        spanishTitle: "",
        userId: 0,
        contentAreaId: props?.standard?.contentAreaId || 0,
        subjectId: props?.standard?.subjectId || 0,
        domainId: props?.standard?.domainId || 0,
        isApproved: false,
        isActive: true,
        videoTags: [],
        gradeLevelId: "",
        gradeLevel: "",
      };
      video = defaultData;
    }
    video.contentAreaId = props?.standard?.contentAreaId || 0;
    video.contentAreaName = props?.standard?.contentAreaName;
    video.subjectId = props?.standard?.subjectId || 0;
    video.subjectName = props?.standard?.subjectName;
    video.domainId = props?.standard?.domainId || 0;
    video.domainName = props?.standard?.domainName;
    video.standardId = props.standard.standardId || 0;
    video.standardName = props.standard.standardName || "";

    navigate(Constant.VIDEO, { state: { objectToEdit: video } });
  };

  function confirmDelete(video: IVideo) {
    if (video.videoId > 0) {
      setShowDeletePopup(true);
      const deleteVideo: IStandardVideo = {
        standardId: video.standardId,
        videoId: video.videoId,
      };
      setVideoDelete(deleteVideo);
    }
  }

  const bindVideos = async (standardId?: number) => {
    setLoading(true);
    const service = await import("../../../api/videos.service");
    service
      .getStandardVideos(standardId ? standardId : props?.standard?.standardId)
      .then((response: any) => {
        setVideos(response.data.videos);
        setLoading(false);
      });
  };

  async function deleteRecord() {
    const service = await import("../../../api/standard.service");
    service.deleteStandardVideo(videoToDelete).then(() => {
      setShowDeletePopup(false);
      setVideoDelete(videoToDelete);
      bindVideos();
    });
  }

  function closeDeletePopup() {
    setShowDeletePopup(false);
    setVideoDelete(defaultStandardVideo);
  }

  function handleVideoClick(videoUrl: string, vttUrl: string | undefined) {
    setTogglePopup(!togglePopup);
    setVideoUrl(videoUrl);
    setVttUrl(vttUrl || null);
  }

  const onVideoAssociate = async () => {
    const standardId: number = +props?.standard?.standardId;
    validateStandardVideoAssociation(videoIds, standardId).then(async (d) => {
      if (d.data.isValid === true) {
        const videoIdsArray = videoIds.split(",").map((id) => Number(id));
        await associateStandardVideos(standardId, videoIdsArray).then(
          (response) => {
            setShowAssociateVideoPopup(false);
            bindVideos();
            toast.info(response.data);
            setVideoIds("");
          }
        );
      } else {
        let message = "<ul class='list-disc'>";
        d.data.linkedVideos.forEach((msg: string) => {
          message = message + "<li class='mt-2'>" + msg + "</li>";
        });
        message = message + "</ul>";
        confirm({
          variant: "info",
          title: "Video already associated",
          description: message,
        });
      }
    });
  };

  return (
    <Fragment>
      <Transition.Root show={togglePopup} as={Fragment}>
        <Dialog
          as="div"
          className="fixed z-10 inset-0 overflow-y-auto"
          onClose={() => setTogglePopup(!togglePopup)}
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <DialogBackdrop className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </TransitionChild>

            {/* This element is to trick the browser into centering the modal contents. */}
            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6">
                <div
                  className="hidden sm:block absolute top-0 right-0 pt-4 pr-4 z-50"
                  onClick={() => setTogglePopup(false)}
                >
                  <button
                    type="button"
                    className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none "
                  >
                    <span className="sr-only">Close</span>
                    <XIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <ReactPlayer
                  url={videoUrl}
                  className="react-player"
                  width="100%"
                  height="auto"
                  controls={true}
                  config={{
                    file: {
                      attributes: {
                        //controlsList: "nodownload",
                        crossOrigin: "anonymous",
                      },
                      tracks: [
                        {
                          kind: "subtitles",
                          src: vttUrl || "",
                          srcLang: "en",
                          label: "English",
                          default: true,
                        },
                      ],
                    },
                  }}
                />
              </div>
            </TransitionChild>
          </div>
        </Dialog>
      </Transition.Root>
      {loading && <Loader />}
      {showDeletePopup && (
        <DeleteModal
          closeDeleteModal={closeDeletePopup}
          deleteRecord={deleteRecord}
        />
      )}
      {showAssociateVideoPopup && (
        <div
          className="fixed z-10 inset-0 overflow-y-auto"
          aria-labelledby="modal-title"
          role="dialog"
          aria-modal="true"
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              aria-hidden="true"
            ></div>

            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>

            <div className="rounded-lg inline-block align-bottom bg-white px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6 h-auto">
              <div className="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
                <button
                  onClick={() => setShowAssociateVideoPopup(false)}
                  type="button"
                  className="bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-text-primary"
                >
                  <XIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>

              <div className="flex border-b">
                <h2 className="text-gray-700 font-semibold text-xl">
                  Enter Video ID(s)
                </h2>
              </div>

              <div className="mt-3 grid grid-cols-2">
                <input
                  value={videoIds}
                  onChange={(e) => setVideoIds(e.target.value)}
                  type="text"
                  autoComplete="off"
                  className=" mt-1 w-full block border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
                />
              </div>
              <div className="flex pt-4">
                <button
                  type="button"
                  className="mt-4 inline-flex items-center text-sm text-white dark:text-white gap-2 bg-primary-color hover:bg-secondary-color py-1.5 px-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color"
                  onClick={() => onVideoAssociate()}
                >
                  Associate
                </button>
                <button
                  type="button"
                  onClick={() => setShowAssociateVideoPopup(false)}
                  className="mt-4 py-1.5 px-4 inline-flex ml-3 items-center text-sm rounded-lg text-gray-700 text-primary-color border border-gray-300 gap-2 bg-white hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color "
                >
                  <span>Cancel</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="text-gray-900 block px-4 py-4 text-sm shadow border-b border-l border-r border-gray-200 rounded-b-lg">
        <div className=" overflow-hidden sm:rounded-lg">
          <div className="flex w-full mt-1 mb-4 rounded-lg">
            <div className="flex justify-end">
              <button
                onClick={() => handleRedirect()}
                type="button"
                className="flex items-center text-sm  cursor-pointer"
              >
                <AddStandardIcon />
                Add Video
              </button>
              <button
                onClick={() => setShowAssociateVideoPopup(true)}
                type="button"
                className="ml-6 flex items-center text-sm  cursor-pointer"
              >
                <AddStandardIcon />
                Associate Video
              </button>
            </div>
          </div>
          <div className="flex flex-wrap">
            {videos.map((video: IVideo) => (
              <div
                className="w-58 shadow-lg bg-white border mr-6 mb-6"
                key={video.videoId}
              >
                <img
                  onClick={() =>
                    handleVideoClick(
                      props.languageId === 2
                        ? video.spanishVideoUrl
                        : video.videoUrl,
                      props.languageId === 2
                        ? video.spanishVttUrl
                        : video.englishVttUrl
                    )
                  }
                  className="rounded-xl overflow-hidden w-full h-28 cursor-pointer"
                  src={video.thumbnail ? video.thumbnail : "/noPreview.png"}
                  alt={
                    video.thumbnail ? video.thumbnail : "No Preview Available"
                  }
                />
                <div className="justify-between px-2 py-2 mt-1">
                  <div className="flex w-full justify-between items-center mb-2">
                    <span className="block text-sm font-semibold text-gray-700">
                      <Link
                        to={Constant.SUBJECTDETAIL.replace(
                          ":id",
                          video.subjectId.toString()
                        )}
                        className="block"
                      >
                        <span>{`${video.subjectName}`}</span>
                      </Link>
                    </span>
                    <DeleteIcon
                      className="w-4 h-4 cursor-pointer hover:opacity-70"
                      onClick={() => confirmDelete(video)}
                    />
                  </div>
                  <div className="flex w-full">
                    <span className="block text-sm font-semibold text-gray-700">
                      <Link
                        to={Constant.VIDEOEDIT.replace(
                          ":id",
                          video.videoId ? video.videoId.toString() : ""
                        )}
                        className="cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
                      >
                        {video.title}
                      </Link>
                    </span>
                  </div>
                  <div className="flex w-full mt-1 pt-1 border-t">
                    {video.videoQuestions?.map((vq) => {
                      return (
                        <>
                          <Link
                            to={Constant.MANAGEQUESTION.replace(
                              ":id",
                              vq.questionId
                            )}
                          >
                            {vq.questionId} ,
                          </Link>
                        </>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Fragment>
  );
}

export default StandardVideos;
